import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API_BASE } from '../config';
import { useTheme } from '../context/ThemeContext';
import Modal from './Modal';
import Icon from './Icon';

interface EventItem {
  id: string;
  type: string;
  title: string;
  body: string | null;
  imageUrl: string | null;
  buttonLabel: string | null;
  buttonTarget: string | null;
  showAsPopup?: boolean;
  popupIntervalHours?: number | null;
  createdAt?: string;
}

// --- show-frequency bookkeeping (localStorage) -------------------------------
const STORAGE_KEY = 'eventPopupShows';
const HOUR = 60 * 60 * 1000;
const DEFAULT_INTERVAL_HOURS = 6;
const DAY = 24 * 60 * 60 * 1000;
const MAX_PER_DAY = 4;

type ShowRecord = { eventId: string; shows: number[] };

function readRecord(): ShowRecord | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed.eventId === 'string' && Array.isArray(parsed.shows)) {
      return { eventId: parsed.eventId, shows: parsed.shows.filter((n: unknown) => typeof n === 'number') };
    }
    return null;
  } catch {
    return null;
  }
}

const recentShows = (rec: ShowRecord | null, eventId: string, now: number): number[] => {
  if (!rec || rec.eventId !== eventId) return [];
  return rec.shows.filter(ts => now - ts < DAY);
};

// Eligible if this is a new event id (always shows once on the next open), OR the
// active event hasn't been shown within its configured interval (default 6h) and
// has been shown fewer than 4 times in the last 24 hours.
function shouldShow(eventId: string, intervalHours?: number | null, now = Date.now()): boolean {
  const shows = recentShows(readRecord(), eventId, now);
  if (shows.length === 0) return true;
  if (shows.length >= MAX_PER_DAY) return false;
  const intervalMs = (intervalHours && intervalHours > 0 ? intervalHours : DEFAULT_INTERVAL_HOURS) * HOUR;
  return now - Math.max(...shows) >= intervalMs;
}

function recordShown(eventId: string, now = Date.now()): void {
  try {
    const shows = recentShows(readRecord(), eventId, now);
    shows.push(now);
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ eventId, shows }));
  } catch {
    // best-effort — a failed write just means the popup may reshow sooner
  }
}

// --- route mapping (mobile route name → web path) ----------------------------
const ROUTE_MAP: Record<string, string> = {
  WalletTopUp: '/wallet-topup',
  'Add funds': '/topup',
  TopUp: '/topup',
  Withdraw: '/withdraw',
  Events: '/events',
  Profile: '/profile',
  Home: '/home',
};

const isUrl = (s: string) => /^https?:\/\//i.test(s.trim());

async function getActiveEvent(): Promise<EventItem | null> {
  try {
    const res = await axios.get<EventItem[]>(`${API_BASE}/home-content`);
    // /home-content returns active, in-schedule items newest-first, so the
    // first flagged showAsPopup is the current popup to surface.
    return res.data.find(c => c.showAsPopup) ?? null;
  } catch {
    return null;
  }
}

export default function EventPopup() {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [event, setEvent] = useState<EventItem | null>(null);
  const [visible, setVisible] = useState(false);

  const check = useCallback(async () => {
    if (visible) return; // don't stack popups
    const ev = await getActiveEvent();
    if (!ev) return;
    if (!shouldShow(ev.id, ev.popupIntervalHours)) return;
    setEvent(ev);
    setVisible(true);
    recordShown(ev.id);
  }, [visible]);

  useEffect(() => {
    check();
    const onVisible = () => { if (document.visibilityState === 'visible') check(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [check]);

  const close = () => setVisible(false);

  const onPressButton = () => {
    const target = event?.buttonTarget?.trim();
    close();
    if (!target) return;
    if (isUrl(target)) {
      window.open(target, '_blank', 'noopener');
    } else {
      navigate(ROUTE_MAP[target] ?? (target.startsWith('/') ? target : `/${target.toLowerCase()}`));
    }
  };

  if (!event) return null;
  const hasButton = !!(event.buttonLabel?.trim() && event.buttonTarget?.trim());

  return (
    <Modal open={visible} onClose={close} card={false} backdropOpacity={0.78}>
      <div style={{
        position: 'relative',
        width: '100%', maxWidth: 400, margin: '0 auto',
        maxHeight: 'calc(100dvh - 48px)',
        borderRadius: 22,
        border: `1px solid ${colors.cardBorder}`,
        backgroundColor: colors.card,
        overflow: 'hidden',
        paddingBottom: 16,
        display: 'flex', flexDirection: 'column',
        animation: 'sls-modal-in 200ms cubic-bezier(0.22, 1, 0.36, 1)',
      }}>
        <button
          aria-label={t('actions.close')}
          onClick={close}
          style={{
            position: 'absolute', top: 10, right: 10, zIndex: 2,
            width: 32, height: 32, borderRadius: 16,
            backgroundColor: 'rgba(0, 0, 0, 0.55)',
            border: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <Icon name="times" size={18} color="#FFFFFF" />
        </button>

        {event.imageUrl && (
          <img
            src={event.imageUrl}
            alt=""
            style={{ width: '100%', height: 180, objectFit: 'cover', backgroundColor: colors.surfaceMuted }}
          />
        )}

        <div style={{ padding: '20px 22px 0', overflowY: 'auto' }}>
          <div style={{ fontSize: 21, fontWeight: 900, color: colors.text, marginBottom: 8 }}>{event.title}</div>
          {event.body && (
            <div style={{ fontSize: 14, lineHeight: 1.5, color: colors.textMuted, whiteSpace: 'pre-wrap' }}>
              {event.body}
            </div>
          )}
        </div>

        {hasButton && (
          <button
            onClick={onPressButton}
            style={{
              margin: '20px 22px 0',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: '15px 0', borderRadius: 999, minHeight: 50,
              backgroundColor: colors.accentDark, color: '#000000',
              border: 'none', cursor: 'pointer',
              fontSize: 15, fontWeight: 800, fontFamily: 'inherit',
            }}
          >
            {event.buttonLabel}
          </button>
        )}

        <button
          onClick={close}
          style={{
            margin: '10px auto 0',
            padding: '8px 12px', minHeight: 44,
            background: 'transparent', border: 'none', cursor: 'pointer',
            fontSize: 14, fontWeight: 700, color: colors.textMuted, fontFamily: 'inherit',
          }}
        >
          {t('actions.close')}
        </button>
      </div>
    </Modal>
  );
}
